import { SliderComponent } from '@/scripts/theme/slider-component'
import { TsDOM as q } from '@/scripts/core/TsDOM'


export class SlideshowComponent extends SliderComponent {
	static override htmlSelector = 'slideshow-component'
	sliderControlWrapper: HTMLElement
	sliderFirstItemNode: HTMLElement
	sliderControlLinksArray?: HTMLElement[]
	wasClicked?: boolean = false
	desktopLayout?: MediaQueryList
	reducedMotion?: MediaQueryList
	autoplaySpeed: number = 0
	sliderAutoplayButton?: HTMLButtonElement
	sliderControlButtons?: NodeListOf<HTMLButtonElement>
	autoplayButtonIsSetToPlay: boolean = false
	setPositionTimeout?: number
	autoplay?: number
	updateButtons?: NodeListOf<HTMLButtonElement>
	constructor() {
		super()
		this.sliderFirstItemNode = q.rs('.slideshow__slide', this.slider)
		this.sliderControlWrapper = q.rs('.slider-buttons', this)
		this.enableSliderLooping = true

		this.updateButtons = q.ol('[data-uc-slideshow-update]', this)
		this.updateButtons?.forEach((button) => {
			button.addEventListener('click', this.onButtonClick.bind(this))
		})
		if (!this.sliderControlWrapper) return

		if (this.sliderItemsToShow && this.sliderItemsToShow.length > 0) this.currentPage = 1

		this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'))
		this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)))
		this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this))
		this.setSlideVisibility()

		if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay()
	}

	setAutoPlay() {
		this.autoplaySpeed = this.slider.dataset.speed ? parseInt(this.slider.dataset.speed) * 1000 : 0
		this.addEventListener('mouseover', this.focusInHandling.bind(this))
		this.addEventListener('mouseleave', this.focusOutHandling.bind(this))
		this.addEventListener('focusin', this.focusInHandling.bind(this))
		this.addEventListener('focusout', this.focusOutHandling.bind(this))

		if (this.querySelector('.slideshow__autoplay')) {
			this.sliderAutoplayButton = q.os('.slideshow__autoplay', this)
			if (!this.sliderAutoplayButton) throw new Error('Autoplay button not found')
			this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this))
			this.autoplayButtonIsSetToPlay = true
			this.play()
		} else {
			;(this.reducedMotion && this.reducedMotion.matches) || (this.desktopLayout && !this.desktopLayout.matches)
				? this.pause()
				: this.play()
		}
	}

	override onButtonClick(event: MouseEvent) {
		super.onButtonClick(event)
		if (!this.sliderItemsToShow)
			throw Error('onButtonClick fired before this.sliderItemsToShow was set, in SlideshowComponent class')
		this.wasClicked = true

		const isFirstSlide = this.currentPage === 1
		const isLastSlide = this.currentPage === this.sliderItemsToShow.length
		const currentTarget = q.oct(event)
		if (!(currentTarget instanceof HTMLButtonElement))
			throw Error('currentTarget is not button element in SlideshowComponent class')

		if (!isFirstSlide && !isLastSlide) {
			return
		}

		if (isFirstSlide && currentTarget.name === 'previous') {
			this.slideScrollPosition =
				this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length
		} else if (isLastSlide && currentTarget.name === 'next') {
			this.slideScrollPosition = 0
		}

		this.setSlidePosition(this.slideScrollPosition ?? 0)
	}

	override setSlidePosition(position: number) {
		if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout)
		this.setPositionTimeout = setTimeout(() => {
			this.slider.scrollTo({
				left: position,
			})
		}, 0)
	}

	override update() {
		super.update()
		if (!this.currentPage) throw Error('this.currentPage is not set in SlideshowComponent class')
		this.sliderControlButtons = q.ol('.slider-counter__link', this)
		this.prevButton?.removeAttribute('disabled')

		if (!this.sliderControlButtons) return

		this.sliderControlButtons.forEach((link) => {
			link.classList.remove('slider-counter__link--active')
			link.removeAttribute('aria-current')
		})

		this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active')
		this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', 'true')
	}

	autoPlayToggle() {
		this.togglePlayButtonState(this.autoplayButtonIsSetToPlay)
		this.autoplayButtonIsSetToPlay ? this.pause() : this.play()
		this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay
	}

	focusOutHandling(event: Event) {
		if (this.sliderAutoplayButton) {
			const eventTarget = q.rt(event)
			const focusedOnAutoplayButton =
				event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(eventTarget)
			if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return
			this.play()
		} else if (
			this.reducedMotion &&
			!this.reducedMotion.matches &&
			this.desktopLayout &&
			this.desktopLayout.matches
		) {
			this.play()
		}
	}

	focusInHandling(event: Event) {
		const eventTarget = q.rt(event)
		if (this.sliderAutoplayButton) {
			const focusedOnAutoplayButton =
				event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(eventTarget)
			if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
				this.play()
			} else if (this.autoplayButtonIsSetToPlay) {
				this.pause()
			}
		}
	}

	play() {
		this.slider.setAttribute('aria-live', 'off')
		clearInterval(this.autoplay)
		this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed)
	}

	pause() {
		this.slider.setAttribute('aria-live', 'polite')
		clearInterval(this.autoplay)
	}

	togglePlayButtonState(pauseAutoplay: boolean) {
		if (!this.sliderAutoplayButton) throw new Error('togglePlayButtonState called early - no autoplay button found')
		if (pauseAutoplay) {
			this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused')
			this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow)
		} else {
			this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused')
			this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow)
		}
	}

	autoRotateSlides() {
		if (!this.sliderItemOffset) throw new Error('autoRotateSlides called early - no sliderItemOffset found')
		const slideScrollPosition =
			this.sliderItems && this.currentPage === this.sliderItems.length
				? 0
				: this.slider.scrollLeft + this.sliderItemOffset

		this.setSlidePosition(slideScrollPosition)
	}

	setSlideVisibility(_event: Event | undefined = undefined) {
		if (this.sliderItemsToShow) {
			this.sliderItemsToShow.forEach((item, index) => {
				const linkElements = item.querySelectorAll('a')
				if (!this.currentPage) throw new Error('setSlideVisibility called early - no currentPage found')
				if (index === this.currentPage - 1) {
					if (linkElements.length)
						linkElements.forEach((button) => {
							button.removeAttribute('tabindex')
						})
					item.setAttribute('aria-hidden', 'false')
					item.removeAttribute('tabindex')
				} else {
					if (linkElements.length)
						linkElements.forEach((button) => {
							button.setAttribute('tabindex', '-1')
						})
					item.setAttribute('aria-hidden', 'true')
					item.setAttribute('tabindex', '-1')
				}
			})
		} else {
			throw new Error('setSlideVisibility called too early')
		}

		this.wasClicked = false
	}

	linkToSlide(event: Event) {
		event.preventDefault()
		if (!this.sliderControlLinksArray || !this.currentPage)
			throw new Error('linkToSlide called early - no sliderControlLinksArray or currentPage found')
		const currentTarget = q.rct(event)
		const slideScrollPosition =
			this.slider.scrollLeft +
			this.sliderFirstItemNode.clientWidth *
				(this.sliderControlLinksArray.indexOf(currentTarget) + 1 - this.currentPage)
		this.slider.scrollTo({
			left: slideScrollPosition,
		})
	}


}

export class SlideshowFade extends SlideshowComponent {
	static override htmlSelector = 'slideshow-fade'
	nextAutoSlide?: number
	variableHeight: boolean = false
	constructor() {
		super()
		if (this.hasAttribute('uc-slideshow-fade-variable-height')) {
			this.variableHeight = true
		}
	}
	override connectedCallback() {
		super.connectedCallback()
		this.setHeight()
	}

	setHeight() {
		const activeSlide = q.rs('[id^="Slide-"].active', this)
		this.slider.style.minHeight = `${activeSlide.scrollHeight}px`
	}

	setFadeActive(number: number, updateCurrentPage?: boolean) {
		if (updateCurrentPage) {
			this.currentPage = number + 1
		}

		this.sliderItems?.forEach((item: HTMLElement, i: number) => {
			if (i === number) {
				item.classList.add('active')
			} else {
				item.classList.remove('active')
			}
		})
		this.sliderControlLinksArray?.forEach((item, i) => {
			if (i === number) {
				item.classList.add('active')
				item.classList.add('slider-counter__link--active')
			} else {
				item.classList.remove('active')
				item.classList.remove('slider-counter__link--active')
			}
		})
	}
	override linkToSlide(event: Event) {
		event.preventDefault()

		if (!this.sliderControlLinksArray || !this.currentPage)
			throw new Error('linkToSlide called early - no sliderControlLinksArray or currentPage found')
		const currentTarget = q.rct(event)
		const number = this.sliderControlLinksArray.indexOf(currentTarget) + 1 - this.currentPage
		this.setFadeActive(number)
	}

	override onButtonClick(event: Event) {
		event.preventDefault()

		const currentTarget = q.rct(event)
		const index = parseInt(q.ra(currentTarget, 'data-uc-slideshow-update'))
		this.setFadeActive(index)
		this.setHeight()
    this.pause()
	}

	override autoRotateSlides() {
		if (this.nextAutoSlide !== undefined) {
			const number = this.nextAutoSlide
			if (this.sliderItems?.length && number == this.sliderItems?.length - 1) {
				this.nextAutoSlide = 0
			} else {
				this.nextAutoSlide = number + 1
			}
			this.setFadeActive(number)
		} else {
			const number = this.currentPage ?? 0

			if (this.sliderItems?.length && number == this.sliderItems?.length - 1) {
				this.nextAutoSlide = 0
			} else {
				this.nextAutoSlide = number + 1
			}
			this.setFadeActive(number)
		}
	}

	override initMobileDraggableSlider() {
		this.slider.addEventListener('touchstart', this.onTouchDown.bind(this), { passive: false })
	}

	onTouchDown(event: TouchEvent) {
		if (!this.sliderItems || !this.currentPage) {
			return
		}
		const initX = event.touches[0].clientX
		const sliderItemsLength = this.sliderItems?.length ?? 0
		const currentPageIndex = this.currentPage ? this.currentPage - 1 : 0
		const onTouchUp = (e: TouchEvent) => {
			if (e.changedTouches.length <= 1) {
				const finalX = e.changedTouches[0].clientX
				const xDelta = finalX - initX
				if (xDelta > 20) {
					if (currentPageIndex === 0) {
						this.setFadeActive(sliderItemsLength - 1, true)
					} else {
						this.setFadeActive(currentPageIndex - 1, true)
					}

				} else if (xDelta < -20) {

					if (currentPageIndex === sliderItemsLength - 1) {
						this.setFadeActive(0, true)
					} else {
						this.setFadeActive(currentPageIndex + 1, true)
					}
				}
			}

			document.removeEventListener('touchend', onTouchUp)
		}
		if (event.touches.length > 1) {
			document.removeEventListener('touchend', onTouchUp)
			return
		}

		document.addEventListener('touchend', onTouchUp)
	}
}
